import type { ResolvedFigure } from '../model.js';
import { compileLessonFigure, type LessonFigureSpec } from './types.js';

/**
 * Figure heights are the one number a lesson still supplies, because the host
 * frame owns them (`viewport.width`/`viewport.height` are the declared host
 * exception in the authoring audit). None of them is eyeballed.
 *
 * Two kinds of family live behind these helpers. Some stretch to whatever
 * viewport they are given — a sequence divides its height by its event count, a
 * terminal fills its frame with rows — and for those the height is arithmetic
 * over the data, reproducing the family's own density rule with one row of
 * slack. The rest lay out at a fixed size and simply stop, and for those
 * `fittedHeight` compiles the figure once, measures the lowest point anything
 * was actually drawn at, and hands back that extent plus a margin, so no scene
 * carries dead space it did not ask for.
 */

/** The engine's own floor: `validateViewport` rejects anything shorter. */
const FLOOR = 380;

/** Tall enough that a fixed-layout family is certainly not being squeezed. */
const PROBE = 900;

/** Terminal evidence: chrome, one row per quoted line, footer, one row of slack. */
export function artifactTerminalHeight(width: number, lineCount: number): number {
  const narrow = width <= 390;
  const content = narrow ? 93 + 20 * lineCount : 97 + 22 * lineCount;
  return Math.max(FLOOR, even(content + 57 + 10));
}

/** Sequence lanes: participant cards, one slot per event, and the lifeline foot. */
export function sequenceHeight(width: number, eventCount: number): number {
  return Math.max(width <= 390 ? 470 : 450, even(190 + eventCount * 46));
}

/** The annotated argument: 3…7 rows that must each stay above the 46px floor. */
export function argumentHeight(width: number, stepCount: number): number {
  const narrow = width <= 390;
  const fixed = (narrow ? 39 + 62 + 20 + 28 : 42 + 58 + 24 + 34) + (narrow ? 8 : 10) * (stepCount - 1);
  return Math.max(FLOOR, even(fixed + stepCount * 68));
}

/**
 * The height a fixed-layout figure actually needs. `fallback` is returned for a
 * family that stretches to its viewport, because such a family can never report
 * an extent smaller than the one it was handed.
 */
export function fittedHeight(build: (height: number) => LessonFigureSpec, fallback: number, margin = 16): number {
  const extent = lowestDrawnPoint(compileLessonFigure(build(PROBE)));
  if (extent > PROBE - 120) return fallback;
  const height = Math.max(FLOOR, even(extent + margin));
  try {
    compileLessonFigure(build(height));
    return height;
  } catch {
    return fallback;
  }
}

/** The lowest y any shape, hit region or anchor of the resolved scene reaches. */
function lowestDrawnPoint(figure: ResolvedFigure): number {
  let lowest = 0;
  for (const shape of figure.shapes) {
    if (shape.kind === 'rect') lowest = Math.max(lowest, shape.box.y + shape.box.height);
    else if (shape.kind === 'polygon') for (const item of shape.points) lowest = Math.max(lowest, item.y);
    else if (shape.kind === 'line') lowest = Math.max(lowest, shape.from.y, shape.to.y);
    else if (shape.kind === 'text') lowest = Math.max(lowest, shape.at.y + shape.size * 0.3);
    else for (const match of shape.d.matchAll(/(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g)) lowest = Math.max(lowest, Number(match[2]));
  }
  for (const hit of figure.hitRegions) lowest = Math.max(lowest, hit.box.y + hit.box.height);
  for (const anchor of figure.anchors) lowest = Math.max(lowest, anchor.point.y);
  return Math.ceil(lowest);
}

/** Even heights keep the half-pixel arithmetic in the compilers on the grid. */
function even(value: number): number {
  return Math.ceil(value / 2) * 2;
}
